import { Component, ContentType, Field } from 'contensis-core-api';
import { ContentTypeField, ContentTypeFields } from './ContentTypeFieldBuilder';
import { FirstPassStub } from './FirstPassStub';
import mapErrorToObject from './mapAppErrorToObject';
import {
  IContensisContext,
  MigrateContentModel,
  MigrateStatus,
  ModelDependencies,
} from '~/models';
import { FormRule } from '~/models/FormContentType';
import {
  ConfirmationRuleReturnNodeId,
  FormContentType,
  FormProperties,
} from '~/models/FormContentTypeManagement';
import ContensisRepository from '~/repositories/ContensisRepository';
import { compareFields } from '~/util/EntryHelper';

/**
 * Build a complete content type specifically for the target repository, providing
 * context of source and target repositories we can check each field in the
 * given source content type for any dependencies and set a two-pass loading status
 * if any dependencies do not exist
 *
 * Output the content type as JSON for loading into the Management API using the `toJSON` method
 */
export class ContentTypeBuilder<
  T extends ContentType | Component | FormContentType = ContentType,
> implements MigrateContentModel
{
  contensis: ContensisRepository;
  source: ContensisRepository;

  resource: T;
  context: IContensisContext;
  confirmationRules: FormProperties['confirmationRules'] = [];
  defaultParentNodeId?: string;
  dependencies: ModelDependencies['dependencies'];
  dependencyOf: ModelDependencies['dependencyOf'];
  existing?: T;
  fields: ContentTypeFields = {};
  status: MigrateStatus;
  error?: MappedError;
  diff?: string;
  /**
   * [defaultId, originalId]
   */
  private _defaults: [string, string][] = [];
  /**
   * [rejectedId, existingId]
   * when we have opted to use an existing default and we might not
   * need to migrate the default entry after all
   */
  private _rejectedDefaults: [string, string][] = [];
  private _requireSecondPass = false;
  /**
   * returns [defaultId, originalId][]
   * a list of defaults to migrate with the resource model
   */
  get defaults(): [string, string][] {
    return [
      ...this._defaults,
      ...Object.values(this.fields)
        .map((f) => f.defaults)
        .flat(),
    ];
  }
  /**
   * returns [rejectedId, existingId][]
   * when we have opted to use an existing default and we might not
   * neeed to migrate the default entry after all
   */
  get rejectedDefaults(): [string, string][] {
    return [
      ...this._rejectedDefaults,
      ...Object.values(this.fields)
        .map((f) => f.rejectedDefaults)
        .flat(),
    ];
  }

  get requireSecondPass() {
    if (this._requireSecondPass) return true;

    return Object.values(this.fields).some((bf) => {
      if (Array.isArray(bf))
        return bf.some((f) => {
          if (f.requireSecondPass) return true;
        });
      else if (bf.requireSecondPass) return true;
    });
  }

  private shouldPublish = (source: T, target: T) =>
    // Compare the source and target resource to check if we are working
    // with a published source and decide whether the target resource should be published
    source.version?.versionNo.endsWith('.0') &&
    !target?.version?.versionNo.endsWith('.0');

  constructor(
    contensis: ContensisRepository,
    source: ContensisRepository,
    resource: T,
    { dependencies, dependencyOf }: ModelDependencies
  ) {
    this.contensis = contensis;
    this.source = source;
    this.resource = resource;

    // Context helper scoped to the target dependencies
    this.context = contensis.context;

    // Find existing resource in target
    this.existing = this.context.resolve.resource(resource.id) as T;

    this.dependencies = dependencies;
    this.dependencyOf = dependencyOf;

    // Build the content type and set the migrate status
    // this.validate();
    this.build();
    this.status = this.checkStatus();
  }

  isContentType = (): this is ContentTypeBuilder<ContentType> =>
    this.contensis.context.assert.isContentType(this.resource);

  get id() {
    return this.resource.id;
  }
  get versionNo() {
    if (!this.existing) return '0.1';
    return this.existing.version?.versionNo;
  }

  /**
   * Iterate over built content type fields and return a the content type as JSON
   * @returns content type as JSON
   */
  toJSON = (opts = { firstPass: false }) => {
    const { resource } = this;
    const { project } = this.contensis;
    const { assert, resolve, transform } = this.context;
    const sourceLanguage = resolve.language(resource);

    const defaultLanguage = project.primaryLanguage || sourceLanguage;
    const description = resource.description?.[sourceLanguage];

    // Map attributes common to ContentType and Component
    let mappedResource = {
      ...resource,
      uuid: undefined,
      id: resource.id,
      name: {
        [defaultLanguage]: resource.name[sourceLanguage],
      },
      description: description
        ? {
            [defaultLanguage]: description,
          }
        : undefined,
      projectId: project.id,
      supportedLanguages: project.supportedLanguages,
    } as typeof resource;

    // The existance of a defaultLanugage key drives ContentType assertions
    if (this.isContentType())
      (mappedResource as ContentType).defaultLanguage = defaultLanguage;

    if (opts.firstPass) {
      // TODO: replace with just the "else" code with opts.firstPass
      // returning firstPassValue for each field

      // use a stub placeholder field for firstPass
      mappedResource = FirstPassStub.contentType(
        mappedResource,
        defaultLanguage
      );
    } else {
      // Map content type fields
      const fields = Object.values(this.fields).reduce((prev, v) => {
        if (Array.isArray(v)) {
          const val: Field[] = [];
          for (const inner of v as ContentTypeField[]) {
            const innerVal = inner.toJSON(opts);
            if (innerVal) val.push(innerVal);
          }
          prev.push(val as any);
        } else {
          const val = v.toJSON(opts);
          if (val) prev.push(val);
        }
        return prev;
      }, [] as Field[]);

      mappedResource.fields = fields;
      if (assert.isContentType(mappedResource) && this.isContentType()) {
        mappedResource.groups = this.resource.groups?.map((group) =>
          transform.RenameKeysAndValues(
            group,
            sourceLanguage,
            project.primaryLanguage
          )
        );
      }
      if (assert.isEntryContentType(mappedResource))
        mappedResource.defaultParentNodeId = this.defaultParentNodeId;
      if (
        assert.isFormContentType(mappedResource) &&
        assert.isFormContentType(resource)
      ) {
        if (resource.properties?.confirmationRules)
          mappedResource.properties = {
            ...((mappedResource.properties || {}) as FormProperties),
            confirmationRules: this.confirmationRules,
          };
      }
    }
    return mappedResource;
  };

  /**
   * Check we have the available requisites to process this content type
   */
  // validate = () => {
  //   // TODO: appears to be useless right now?
  //   if (!this.resource) {
  //     this.status = 'not found';
  //     this.error = mapErrorToObject(
  //       new Error(
  //         `Content type '${this.resource.id}' does not exist in project '${this.contensis.project.id}'`
  //       )
  //     );
  //   }
  // };

  /**
   * Iterate all fields in the content type taking the values from the source content type
   * adding each field to the built content type fields
   */
  build = () => {
    const { resource } = this;
    try {
      for (const field of resource.fields || []) {
        this.fields[field.id] = new ContentTypeField(
          field,
          undefined,
          this.existing?.fields?.find((f) => f.id === field.id),
          this.resource,
          this.context,
          this.contensis
        );
      }
      this.#buildDefaultParentNodeId();
      this.#buildFormSpecificAttributes();
    } catch (ex: any) {
      this.error = mapErrorToObject(
        ex || new Error(`Error building content type: ${this.resource.id}`)
      );
    }
  };

  #findNodeToMigrate = (originalId: string) =>
    this.contensis.nodes.migrateNodes.find(
      (mn) => mn.originalId === originalId
    );

  #buildDefaultParentNodeId = () => {
    const { context, existing, resource } = this;
    if (
      context.assert.isEntryContentType(resource) &&
      resource.defaultParentNodeId
    ) {
      if (
        existing &&
        context.assert.isEntryContentType(existing) &&
        existing.defaultParentNodeId
      ) {
        // always map an existing node id first
        const existingDefaultParentNodeId = existing.defaultParentNodeId;
        if (existingDefaultParentNodeId) {
          this._defaults.push([
            existingDefaultParentNodeId,
            resource.defaultParentNodeId,
          ]);
          this.defaultParentNodeId = existingDefaultParentNodeId;
          this._rejectedDefaults.push([
            resource.defaultParentNodeId,
            existingDefaultParentNodeId,
          ]);
        }
      } else {
        // check if we're avoiding defaults
        if (this.context.payload.includeDefaults) {
          // find the node id we're going to migrate, or undefined
          const node = this.#findNodeToMigrate(resource.defaultParentNodeId);
          if (node) {
            this._defaults.push([node.id, node.originalId]);
            this.defaultParentNodeId = node.id;
          }
        }
      }
    }
  };

  #buildFormSpecificAttributes = () => {
    const { context, existing, resource } = this;
    if (context.assert.isFormContentType(resource)) {
      const buildRule = (rule: FormRule<ConfirmationRuleReturnNodeId>) => {
        const nodeId = rule.return.link.sys.node.id;
        const node = this.#findNodeToMigrate(nodeId);
        // Update the node id in the return link rule
        if (node) {
          this._defaults.push([node.id, node.originalId]);
          this.confirmationRules.push({
            return: { link: { sys: { node: { id: node.id } } } },
          });
        }
      };
      for (const rule of resource.properties?.confirmationRules || []) {
        if (!context.assert.form.isNodeIdReturnRule(rule)) {
          // handle any other rule type from source content type
          this.confirmationRules.push(rule);
        } else {
          // handle redirect site view rule type
          if (existing && context.assert.isFormContentType(existing)) {
            const existingReturnLink =
              existing.properties?.confirmationRules.find((rule) =>
                context.assert.form.isNodeIdReturnRule(rule)
              );
            if (existingReturnLink) {
              this.confirmationRules.push(existingReturnLink);
            } else {
              buildRule(rule);
            }
          } else {
            buildRule(rule);
          }
        }
      }
    }
  };

  /**
   * Check the resource for update status
   */
  checkStatus = (): MigrateStatus => {
    const { context, dependencies, error, existing, resource } = this;

    if (this.status) return this.status;
    if (error) return 'error';

    if (existing) {
      // Has the resource changed from the existing?
      const resource = this.toJSON();

      // Set Contensis controlled fields to null before doing the diff as these will always be different

      const { changed, diff } = compareFields(
        resource,
        existing,
        this.source,
        this.contensis,
        // Contensis managed fields, we won't argue about these being different
        [
          'canAddChildComponents',
          'canBeAddedAsChildComponent',
          'defaultLanguage',
          'isChild',
          'hasChildren',
          'previewUrl',
          'projectId',
          'supportedLanguages',
          'uuid',
          'version',
          'uploadPath',
          'enabled', // added for tests
          'reviews', // new field not sure if we're supporting this yet
        ]
      );

      if (changed) {
        this.diff = diff;
        return 'update';
      } else {
        if (this.shouldPublish(resource, existing)) return 'publish';
        return 'no change';
      }
    }

    if (this.requireSecondPass) return 'two-pass';

    // Check if we have dependencies
    const contentTypeIds = dependencies?.contentTypes || [];
    // Check if components have components in them
    // Don't consider content types containing components as two-pass
    const componentIds = context.assert.isComponent(resource)
      ? dependencies?.components || []
      : [];

    // Do the dependencies already exist in the target?
    for (const id of [...contentTypeIds, ...componentIds])
      if (!context.resolve.resource(id)) {
        return 'two-pass';
      }

    return 'create';
  };
}
